import { generateIdempotencyKey } from '@/api/client/http';
import type { AttributeProposal, CategoryProposal } from '@/api/schemas/ai-assistance';
import type { AttributeDraft, CategoryDraft } from '@/modules/catalog/pages/setup-page/types';

/**
 * Turning a proposal into editable drafts.
 *
 * ---------------------------------------------------------------------------
 * A PROPOSAL IS NOT YET A BODY
 * ---------------------------------------------------------------------------
 * Two things are decided here rather than at the moment of the write, because
 * the merchant sees them and can correct them:
 *
 *   - the SLUG, which the catalogue API requires in lower kebab-case. When the
 *     proposal did not give one, it is derived from the name — never empty.
 *   - the ATTRIBUTE KEY, which the dictionary requires in lower snake case.
 *
 * Neither is invented silently: both are editable, and the derived value is
 * visible in the field the merchant is looking at.
 */

/** The attribute key the dictionary accepts: 2-64 chars, lower snake case. */
const ATTRIBUTE_KEY_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;

/** Lower kebab-case, as the catalogue API's slug rule wants it. */
export function slugify(value: string): string {
  const kebab = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 160)
    .replace(/-+$/g, '');
  return kebab;
}

/** A dictionary key derived from a name, always one the API accepts. */
export function attributeKeyFrom(value: string): string {
  const cleaned = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const body = cleaned.replace(/^[^a-z]+/, '');
  if (body.length >= 2) return body.slice(0, 64);
  return `attr_${body}`.slice(0, 64);
}

/** True when the key already satisfies the dictionary's own rule. */
export function isValidAttributeKey(key: string): boolean {
  return ATTRIBUTE_KEY_PATTERN.test(key);
}

/** The option values a select attribute carries, from a comma separated list. */
export function parseOptions(text: string): ReadonlyArray<string> {
  return text
    .split(',')
    .map((option) => option.trim())
    .filter((option) => option !== '');
}

/** The kinds that carry option values, and refuse a body without them. */
export function kindNeedsOptions(kind: string): boolean {
  return kind === 'single_select' || kind === 'multi_select';
}

/** The editable copy of one proposed category. */
export function buildCategoryDrafts(proposal: CategoryProposal): ReadonlyArray<CategoryDraft> {
  return proposal.proposed.map((item, index) => ({
    id: `category-${index}`,
    idempotencyKey: generateIdempotencyKey(),
    name: item.name,
    slug: item.slug.trim() === '' ? slugify(item.name) : item.slug.trim(),
    description: item.description ?? '',
    parentCategoryId: item.parent?.existingCategoryId ?? '',
    parentProposedName: item.parent?.proposedCategoryName ?? '',
    uncertain: item.uncertain,
    // Nothing is accepted until the merchant says so.
    accepted: false,
    outcome: null,
  }));
}

/** The editable copy of one proposed attribute. */
export function buildAttributeDrafts(proposal: AttributeProposal): ReadonlyArray<AttributeDraft> {
  return proposal.proposed.map((item, index) => ({
    id: `attribute-${index}`,
    idempotencyKey: generateIdempotencyKey(),
    key: isValidAttributeKey(item.key) ? item.key : attributeKeyFrom(item.name),
    name: item.name,
    kind: item.kind,
    description: item.description ?? '',
    variantAxis: item.variantAxis,
    optionsText: item.options.join(', '),
    assignToCategoryIds: item.assignToCategoryIds,
    uncertain: item.uncertain,
    accepted: false,
    outcome: null,
  }));
}
